import h from 'hyperscript';

import { compareWithLocale } from '@disy/lib/locale';
import { logger } from '@disy/lib/logging';

import { alert, confirm } from 'cadenza/alert';
import type { ItemAuthorizationApi } from 'cadenza/api-client/access-manager-api';
import type { ObjecttypeMemberDto } from 'cadenza/api-client/dto/objecttype-member-dto';
import { checkWhetherObjecttypeIsUsedInAnyWorkbook, getConnectedObjecttypes } from 'cadenza/api-client/objecttypes-api';
import { type Authority, getSrsMaxExtent } from 'cadenza/api-client/srs-api';
import { getReferenceSystemList, getReferenceSystemSettings } from 'cadenza/api-client/srs-config-api';
import type { DimensionMeasureAttributes } from 'cadenza/objecttype/objecttype-details/dimension-measure-attributes';
import i18n from 'cadenza/objecttype/objecttype-details/objecttype-utils.properties';
import type { ObjecttypeIdString } from 'cadenza/repository/model/objecttype-id.gen';
import { isProblem } from 'cadenza/utils/problem-util';

export function getConnectedObjecttypesGroupedAndSorted(sourceObjecttypeId: ObjecttypeIdString, includeMembers = true) {
  return getConnectedObjecttypes(sourceObjecttypeId, includeMembers).then((connectedObjecttypesDtos) =>
    connectedObjecttypesDtos
      .map((connectedObjecttype) => ({
        ...connectedObjecttype,
        attributeGroups: toDimensionMeasureAttributes(connectedObjecttype.objecttypeMembers),
      }))
      .sort((a, b) => compareWithLocale(a.objecttype.printName, b.objecttype.printName)),
  );
}

function toDimensionMeasureAttributes(objecttypeMembers: ObjecttypeMemberDto[]): DimensionMeasureAttributes {
  const dimensions: ObjecttypeMemberDto[] = [];
  const measures: ObjecttypeMemberDto[] = [];
  objecttypeMembers.forEach((member) => {
    if (member.attribute) {
      if (member.attribute.dimension) {
        dimensions.push(member);
      } else {
        measures.push(member);
      }
    } else if (member.dateHierarchy || member.multiAttributeHierarchy) {
      dimensions.push(member);
    }
  });
  return {
    dimensions,
    measures,
  };
}
export async function toggleObjecttypeVisibility(
  showInWorkbook: boolean,
  authorizationApi: ItemAuthorizationApi,
  objecttypeId: ObjecttypeIdString,
  submitCallback: () => Promise<void>,
  resetCallback: () => void,
): Promise<void> {
  if (showInWorkbook) {
    await submitCallback();
  } else {
    try {
      if ((await withCheckWhetherExistingUsagesAffected(submitCallback, authorizationApi, objecttypeId)) === false) {
        resetCallback();
      }
    } catch (error) {
      logger.error('Error while checking whether objecttype is shared among users or groups', error);
      resetCallback();
      // Re-throw the error so that the toggle is reset as well.
      throw error;
    }
  }
}

async function withCheckWhetherExistingUsagesAffected(
  submitCallback: () => Promise<void>,
  authorizationApi: ItemAuthorizationApi,
  objecttypeId: ObjecttypeIdString,
) {
  const [shared, usedInWorkbook] = await Promise.all([
    authorizationApi.isItemShared(),
    checkWhetherObjecttypeIsUsedInAnyWorkbook(objecttypeId),
  ]);
  if ((shared || usedInWorkbook) && !(await createObjectTypeSharedOrUsedConfirmDialog())) {
    return false;
  }
  return submitCallback();
}

function createObjectTypeSharedOrUsedConfirmDialog() {
  return confirm(i18n('objecttype.shared.warning.title'), createWarningForUsedOrSharedObjecttype(), {
    type: 'warning',
    submit: i18n('objecttype.shared.warning.submit'),
  });
}

function createWarningForUsedOrSharedObjecttype() {
  return h('.d-stack-v.space-3', [
    h('p', i18n('objecttype.shared.warning.message')),
    h('ul', [
      h('li', i18n('objecttype.shared.warning.message.usersOrGroups')),
      h('li', i18n('objecttype.shared.warning.message.workbooks')),
    ]),
  ]);
}

export function showErrorAlertForVisibilityToggle(message: string, error: unknown) {
  logger.error(message, error);
  let title = '';
  let effectiveMessage = message;
  if (isProblem(error)) {
    if (error.key === 'objecttype-cant-modify-enrichment') {
      title = message;
      effectiveMessage = i18n('objecttype-cant-modify-enrichment');
    } else {
      title = error.title;
    }
  }
  alert(title, effectiveMessage, { type: 'error' });
}

export async function isCdsSrsWithMaxExtentConfigured() {
  const srsAuthorityCode = await getCdsSrsAuthorityCode();
  const maxExtent = await getSrsMaxExtent(srsAuthorityCode);
  return maxExtent !== undefined;
}

export async function getCdsSrsAuthorityCode() {
  const epsg3857 = 'EPSG:3857' as Authority;
  const srsSettings = await getReferenceSystemSettings();
  const configuredCdsReferenceSystemId = srsSettings?.cadenzaManagedData?.referenceSystemId;
  if (configuredCdsReferenceSystemId) {
    const srsList = await getReferenceSystemList();
    const configuredCdsSrsAuthorityCode = srsList.find(
      (srsDto) => srsDto.referenceSystemId === configuredCdsReferenceSystemId,
    )?.authority;
    return configuredCdsSrsAuthorityCode ?? epsg3857;
  }
  return epsg3857;
}

export function getObjecttypeCanBeUsedAsTemplateImpedimentsMessages(canBeUsedAsTemplateImpediments: string[]) {
  return canBeUsedAsTemplateImpediments.map((messageKey) =>
    i18n(`objecttype.canBeUsedAsTemplateImpediments.message.<${messageKey}>`),
  );
}
